/**
 * `verify-field` — FR-07, CR-01, NFR-07, NFR-12.
 *
 * This is where the second half of the ISO 42001 evidence chain is created. An
 * AI-extracted value plus the row recording that a named human confirmed or
 * corrected it *is* the evidence for that field — CLAUDE.md rule 7. Not a
 * convenience, not a data-quality nicety: the artefact.
 *
 * Which is why this endpoint returns an {@link AuditReceiptSchema} and why the
 * response carries the updated provenance rather than a bare `ok`.
 */
import { z } from 'zod';
import { ContractKeySchema, IsoTimestampUtcSchema, OpaqueIdSchema, PartyKeySchema, } from '../primitives.js';
import { AuditReceiptSchema, HumanVerificationSchema } from '../provenance.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { FieldNameSchema, VerificationQueueItemSchema } from '../domain/verification.js';
import { ContractTypeSchema, DataTrustSchema, ProcessingStatusSchema, VerificationStatusSchema } from '../vocabulary.js';
import { TagFilterSchema } from './contract-tags.js';
/* -------------------------------------------------------------------------- */
/* Reading the queue                                                           */
/* -------------------------------------------------------------------------- */
export const VerificationQueueRequestSchema = z
    .object({
    /** Narrow to one contract's outstanding fields. */
    contract_key: ContractKeySchema.optional(),
    party_key: PartyKeySchema.optional(),
    /** CLM-77 — all-of filter on the contract's own tags. */
    tags: TagFilterSchema.optional(),
    /** CLM-86 — one field path, exact match. */
    field_name: FieldNameSchema.optional(),
    /**
     * CLM-86 — one contract type, exact match. CLM-118 (B19): a filter for `OTHER`
     * lists real `OTHER` contracts only — a Type pending row (`OTHER` + `pending`)
     * is never returned for a type filter, in the queue or its totals.
     */
    contract_type: ContractTypeSchema.optional(),
    /** FR-07 — the high-stakes subset is the queue that actually has an SLA. */
    high_stakes_only: z.boolean().optional(),
    /** Only rows the aging signal has escalated (§3.1 `vw_verification_aging`). */
    escalated_only: z.boolean().optional(),
    /** Omit to see `Pending` only. */
    status: z.array(VerificationStatusSchema).min(1).optional(),
    /** Oldest first by default: aging is the whole point of the queue. */
    sort: z.enum(['oldest_first', 'high_stakes_first']).optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
export const VerificationQueueResponseSchema = z.object({
    items: z.array(VerificationQueueItemSchema),
    page: PageInfoSchema,
    /** Counters for the admin dashboard, within the caller's scope. */
    totals: z.object({
        pending: z.number().int().min(0),
        pending_high_stakes: z.number().int().min(0),
        escalated: z.number().int().min(0),
        /** Age of the oldest unverified high-stakes field, in days. */
        oldest_pending_age_days: z.number().int().min(0).nullable(),
    }),
});
/* -------------------------------------------------------------------------- */
/* Verifying                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * The three outcomes, discriminated so each carries exactly what it needs.
 *
 * `confirm` and `correct` are kept apart deliberately. NFR-12 requires extraction
 * accuracy to be *measured* on real contracts, and the correction rate is the
 * measurement — a single "verified" verb that quietly accepted a changed value
 * would destroy the only accuracy signal this system generates for free.
 */
export const VerifyFieldOutcomeSchema = z.discriminatedUnion('outcome', [
    z
        .object({
        /** The extracted value was right. Recorded as `Verified`. */
        outcome: z.literal('confirm'),
    })
        .strict(),
    z
        .object({
        /** The extracted value was wrong. Recorded as `Corrected`. */
        outcome: z.literal('correct'),
        /**
         * The value a human settled on. `null` where the correct answer is *"this
         * contract does not state one"* — a distinct and useful correction, because it
         * tells the extractor it hallucinated a value rather than mis-read one.
         */
        confirmed_value: z.string().max(400).nullable(),
    })
        .strict(),
    z
        .object({
        /**
         * The field cannot be settled from this document — ambiguous drafting, an
         * unreadable scan, a term genuinely absent. Recorded as `Rejected`.
         *
         * This is not "verified": it stays visible, and it does not stop escalation.
         * A field that silently disappeared from the queue because someone could not
         * read it is precisely how FR-07 fails.
         */
        outcome: z.literal('reject'),
        reason: z.string().min(1).max(1000),
    })
        .strict(),
    z
        .object({
        /**
         * CLM-78 (B5) — **Accept extracted**: the spelling found in the document is
         * this party's name. Only for `parties.primary.counterparty_name`; any other
         * `field_name` is 400 `outcome_not_applicable`. Approves the `Detected` alias
         * (FR-29), updates the display name for a **CLM-registered** vendor only
         * (A5 — Qfly/Dynamics names are read-only caches, DR-04), re-queues the held
         * contract and settles the row `Corrected`. Gate `party:manage_alias` (A2).
         */
        outcome: z.literal('accept_extracted'),
    })
        .strict(),
]);
export const VerifyFieldRequestSchema = z
    .object({
    verification_id: OpaqueIdSchema,
    /**
     * Sent alongside `verification_id` and cross-checked server-side. Redundant on
     * purpose: it makes a mis-wired front end that verified the wrong row a
     * `validation_failed` rather than a silent false attestation, and a false
     * attestation in the evidence chain is the worst failure this endpoint has.
     */
    contract_key: ContractKeySchema,
    field_name: FieldNameSchema,
    outcome: VerifyFieldOutcomeSchema,
    /**
     * The value the reviewer was shown. Optimistic concurrency: if re-extraction
     * changed it since the queue was rendered, the verification is rejected as a
     * `conflict` rather than attesting to a value nobody read.
     */
    seen_extracted_value: z.string().max(400).nullable(),
    /** Free-text note, recorded on the audit row. */
    note: z.string().min(1).max(1000).optional(),
})
    .strict();
export const VerifyFieldResponseSchema = z.object({
    verification_id: OpaqueIdSchema,
    contract_key: ContractKeySchema,
    field_name: FieldNameSchema,
    /** The value now held for this field. */
    effective_value: z.string().max(400).nullable(),
    /**
     * The updated verification half of the field's provenance — the CR-01 pairing,
     * returned so the front end holds the same evidence the database now does.
     */
    verification: HumanVerificationSchema,
    /**
     * Recomputed after this verification. May advance once the last outstanding
     * field on the contract is settled — which is why it is returned rather than
     * left for the caller to infer.
     */
    contract_processing_status: ProcessingStatusSchema,
    /**
     * CLM-108 — the contract's trust after this settle: the trust advance's own
     * `RETURNING data_trust` when it fired, else the locked row's. Settling the
     * last human field beside auto-approved ones yields `AutoApproved`, never
     * `Verified` (D-2.1-4), so a receipt reads from this, not from
     * `contract_processing_status`, whose `Verified` is only the pipeline's end.
     */
    contract_data_trust: DataTrustSchema,
    /** Fields still outstanding on this contract. `AutoApproved` is settled, so it is not counted (FR-32). */
    remaining_pending_count: z.number().int().min(0),
    verified_utc: IsoTimestampUtcSchema,
    audit: AuditReceiptSchema,
});
//# sourceMappingURL=verify-field.js.map